import React, { useState, useEffect, useRef } from "react";
import { useChatSocket } from "../../../services/support.service";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import DateSeparator from "./DateSeparator";
import ConnectionStatus from "./ConnectionStatus";
import { useChatMessages } from "../../../hooks/useChatMessages";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { User, Message } from "../../../types/support.types";
import { TitleArea } from "../../../components/Typography/titleArea";

interface UserSupportScreenProps {
  currentUser: User;
}

const UserSupportScreen: React.FC<UserSupportScreenProps> = ({
  currentUser,
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [isAdminOnline, setIsAdminOnline] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { getChatSocket, isConnected } = useChatSocket("user");

  const chatSocket = getChatSocket();
  const {
    messagesContainerRef,
    scrollToBottom,
    formatTime,
    groupMessagesByDate,
  } = useChatMessages(messages);

  const {
    selectedFile,
    setSelectedFile,
    handleFileSelect,
    handleRemoveFile,
    convertFileToBase64,
  } = useFileUpload(10);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!chatSocket || !isConnected) return;

    chatSocket.emit("chat:load-messages", {
      conversationId: conversationId || 0,
      userId: parseInt(currentUser.id),
    });

    chatSocket.on(
      "chat:messages-loaded",
      (data: { messages: Message[]; conversationId?: number }) => {
        setMessages(data.messages.reverse());
        if (data.conversationId) {
          setConversationId(data.conversationId);
        }
      }
    );

    chatSocket.on("chat:new-message", (newMessage: Message) => {
      setMessages((prev) => [...prev, newMessage]);
      scrollToBottom();

      if (conversationId && newMessage.sender_type === "admin") {
        chatSocket.emit("chat:mark-as-read", {
          conversationId: conversationId,
          userId: parseInt(currentUser.id),
        });
      }
    });

    chatSocket.on("chat:message-sent", (sentMessage: Message) => {
      if (!conversationId) {
        setConversationId(sentMessage.conversation_id);
      }
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === sentMessage.id);
        if (!exists) {
          return [...prev, sentMessage];
        }
        return prev;
      });
      scrollToBottom();
    });

    chatSocket.on("chat:admin-assigned", () => {
      setIsAdminOnline(true);
    });

    chatSocket.on("chat:error", (error: { message: string }) => {
      console.error("Chat error:", error.message);
    });

    return () => {
      chatSocket.off("chat:messages-loaded");
      chatSocket.off("chat:new-message");
      chatSocket.off("chat:message-sent");
      chatSocket.off("chat:admin-assigned");
      chatSocket.off("chat:error");
    };
  }, [chatSocket, isConnected, currentUser.id, conversationId]);

  const handleSendMessage = async () => {
    if (!chatSocket || (!message.trim() && !selectedFile)) return;

    setUploading(true);
    try {
      let imageData = null;
      let imageType = null;

      if (selectedFile) {
        imageData = await convertFileToBase64(selectedFile);
        imageType = selectedFile.type;
      }

      chatSocket.emit("chat:send-message", {
        conversationId,
        message: message.trim() || (selectedFile ? "Attachment" : ""),
        senderId: parseInt(currentUser.id),
        senderType: "user",
        image: imageData,
        imageType: imageType,
      });

      setMessage("");
      handleRemoveFile(fileInputRef);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex md:flex-row flex-col items-center md:items-start w-full h-screen pb-8">
      <div className="flex flex-col w-full h-full items-start px-4 py-3">
        <div className="flex items-center justify-between w-full mb-4">
          <TitleArea
            variant="page"
            title="Valueit Support Team"
            subTitleContent={<ConnectionStatus isConnected={isConnected} />}
          />
        </div>

        <hr className="hr-line w-full my-2" />

        <div className="vi-glass-card flex-1 flex flex-col w-full min-h-0 overflow-hidden">
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto px-6 py-6"
          >
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-400">
                  <p className="text-lg">
                    Start a conversation with our support team
                  </p>
                  <p className="text-sm mt-2">
                    Type your message below to begin
                  </p>
                </div>
              </div>
            )}

            {messages.length > 0 && (
              <>
                {Object.entries(messageGroups).map(
                  ([dateLabel, dateMessages]) => (
                    <div key={dateLabel}>
                      <DateSeparator label={dateLabel} />

                      <div className="space-y-6 mb-8">
                        {dateMessages.map((msg) => (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            isOwnMessage={msg.sender_type === "user"}
                            senderName={
                              msg.sender_type === "user" ? "You" : "Admin"
                            }
                            senderInitial="A"
                            formatTime={formatTime}
                            isSystemMessage={
                              msg.isSystem === 1 || msg.sender_name === "System"
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </>
            )}
          </div>

          <MessageInput
            message={message}
            setMessage={setMessage}
            onSend={handleSendMessage}
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            onRemoveFile={() => handleRemoveFile(fileInputRef)}
            isConnected={isConnected}
            uploading={uploading}
            placeholder="Message"
            attachButtonVariant="primary"
          />
        </div>
      </div>
    </div>
  );
};

export default UserSupportScreen;
